import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Alert, ModalBody, ModalFooter } from 'reactstrap';
import type { SerializedSongPublish } from 'fym-shared/src/music24/songPublish';
import { TypedMemo } from '../../../common/types';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { songUploadStatusKind } from '../songUpload';
import { soundCloudStatusMessages as m } from '../soundCloudMessages';
import { songUploadStatusTexts } from './songUploadStatusTexts';
import { useSongUploadStatusActions } from './useSongUploadStatusActions';
import { SongUploadWhen } from './SongUploadWhen';
import { songUploadErrorText } from './songUploadErrorText';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for SongUpload; each part uses its own slice of it */
import styles from './SongUpload.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongUploadStatusProps {
  accountId: number;
  record: SerializedSongPublish;
  releaseDate: string | null;
  loginExpired: boolean;
  onUploadAgain: () => void;
  onReconnect: () => void;
  onClose: () => void;
}

const TONE = { ok: styles.chipOk, work: styles.chipWork, bad: styles.chipBad, muted: styles.chipMuted };

/** The status panel of a SoundCloud upload (design c6). Every member sees the same buttons (D12). */
const SongUploadStatusComponent: React.FC<SongUploadStatusProps> = ({
  accountId,
  record,
  releaseDate,
  loginExpired,
  onUploadAgain,
  onReconnect,
  onClose,
}) => {
  const intl = useIntl();
  const { showSuccess } = useShowToast();
  const kind = songUploadStatusKind(record, loginExpired);
  const texts = songUploadStatusTexts(intl, kind, record);
  const actions = useSongUploadStatusActions({ accountId, record, releaseDate, onCanceled: onClose });
  const url = record.externalUrl;
  const copy = useCallback(() => {
    if (url === null) {
      return;
    }
    navigator.clipboard
      ?.writeText(url)
      .then(() => showSuccess(intl.formatMessage(m.linkCopied)))
      .catch(console.error);
  }, [intl, showSuccess, url]);
  const isLive = kind === 'live' || kind === 'private';

  return (
    <>
      <ModalBody className={styles.body} data-testid={`song-upload-status-${kind}`}>
        <div className={styles.statusHead}>
          <div className={styles.headText}>
            <b>{withBrandWord(texts.head)}</b>
            {texts.sub !== '' && <small>{withBrandWord(texts.sub)}</small>}
          </div>
          <span className={clsx(styles.chip, TONE[texts.tone])}>{texts.chip}</span>
        </div>
        {isLive && url !== null && <div className={styles.linkRow}>{url.replace(/^https?:\/\//, '')}</div>}
        {texts.isAlert ? (
          <Alert color="danger" className="mb-0">
            {withBrandWord(texts.body)}
          </Alert>
        ) : (
          <span>{withBrandWord(texts.body)}</span>
        )}
        {actions.isEditing && (
          <SongUploadWhen
            timings={actions.timings}
            value={actions.timing}
            sharing={record.sharing}
            releaseDate={releaseDate}
            onChange={actions.setTiming}
          />
        )}
      </ModalBody>
      <ModalFooter className={styles.foot}>
        {isLive && url !== null && (
          <>
            <a
              className={clsx('btn', kind === 'live' ? 'btn-primary' : 'btn-outline-secondary')}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {intl.formatMessage(m.openOnSoundCloud)}
            </a>
            <button type="button" className="btn btn-link" onClick={copy}>
              {intl.formatMessage(m.copyLink)}
            </button>
          </>
        )}
        {kind === 'waiting' && !actions.isEditing && (
          <button type="button" className="btn btn-outline-secondary" onClick={actions.startEditing}>
            {intl.formatMessage(m.changeTime)}
          </button>
        )}
        {kind === 'waiting' && actions.isEditing && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={actions.isBusy}
            onClick={() => actions.saveTime().catch(console.error)}
          >
            {intl.formatMessage(m.saveTime)}
          </button>
        )}
        {kind === 'waiting' && (
          <button
            type="button"
            className="btn btn-link text-danger"
            disabled={actions.isBusy}
            onClick={() => actions.cancelUpload().catch(console.error)}
          >
            {intl.formatMessage(m.cancelUpload)}
          </button>
        )}
        {kind === 'reconnect' && (
          <button type="button" className="btn btn-primary" onClick={onReconnect}>
            {intl.formatMessage(m.reconnect)}
          </button>
        )}
        {(kind === 'removed' || kind === 'failed' || kind === 'refused' || kind === 'unsure' || kind === 'master') && (
          <button type="button" className="btn btn-primary" onClick={onUploadAgain}>
            {intl.formatMessage(kind === 'removed' ? m.uploadAgain : m.tryAgain)}
          </button>
        )}
        {actions.error !== null && (
          <span className={styles.error} role="alert">
            {withBrandWord(songUploadErrorText(intl, actions.error))}
          </span>
        )}
      </ModalFooter>
    </>
  );
};

export const SongUploadStatus = TypedMemo(SongUploadStatusComponent);
